// src/components/AdminEditor.jsx
import React, { useEffect, useState } from "react";

const CONFIG_URL =
  "https://amarraqrbucket.s3.us-east-1.amazonaws.com/qr-config.json";
const ADMIN_ENDPOINT =
  "https://rjjhtelck7.execute-api.us-east-1.amazonaws.com/amarraAdmin";
const ADMIN_TOKEN =
  "X9v7rP3tQ5m1kZ2aW4uE6oB8yC0jL7dR9hF2nG5pV8qT1sU4iM6eK0wA3bY7cO9";

// --- helpers ---
function isValidMonthKey(v) {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
}

function isLikelyDisplayDate(v) {
  return /^\d{2}\/\d{2}\/\d{4}$/.test(v);
}

function niceMonthLabel(monthKey) {
  try {
    const [y, m] = monthKey.split("-");
    const d = new Date(`${y}-${m}-01T00:00:00`);
    return d.toLocaleString("en-US", { month: "short", year: "numeric" });
  } catch {
    return monthKey;
  }
}

function isStillValid(monthData) {
  if (!monthData?.expires) return false;

  const [month, day, year] = monthData.expires.split("/").map(Number);

  // Keep the month visible through the full expiration day.
  // Example: Apr 2026 expires 05/10/2026, so it disappears on 05/11/2026.
  const expirationDate = new Date(year, month - 1, day, 23, 59, 59);
  const today = new Date();

  return today <= expirationDate;
}

export default function AdminEditor() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  // BLANK form
  const [month, setMonth] = useState("");
  const [code, setCode] = useState("");
  const [link, setLink] = useState("");
  const [expires, setExpires] = useState("");

  // Load existing JSON for list/editing
  useEffect(() => {
    fetch(CONFIG_URL, { cache: "no-store" })
      .then((r) => r.json())
      .then((json) => {
        setData(json || {});
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load current config.");
        setLoading(false);
      });
  }, []);

  const validate = () => {
    if (!isValidMonthKey(month)) {
      return "Month must be in the format YYYY-MM (01–12).";
    }

    if (!code.trim()) {
      return "Gate code is required.";
    }

    if (!link.startsWith("https://")) {
      return "Tap-to-open link must start with https://";
    }

    if (!isLikelyDisplayDate(expires)) {
      return "Expiration date should be MM/DD/YYYY.";
    }

    return "";
  };

  // Combined: add/update + upload to S3 (presigned PUT) in one click
  const saveMonth = async () => {
    const v = validate();

    if (v) {
      setError(v);
      setSaved(false);
      return;
    }

    setError("");
    setSaved(false);
    setSaving(true);

    const next = { ...(data || {}), [month]: { code, link, expires } };

    try {
      // Get presigned URL
      const presignRes = await fetch(ADMIN_ENDPOINT, {
        headers: { "x-admin-token": ADMIN_TOKEN },
      });

      if (!presignRes.ok) {
        throw new Error(`Presign failed: ${presignRes.status}`);
      }

      const { url } = await presignRes.json();

      // PUT new JSON to S3
      const putRes = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "max-age=60, must-revalidate",
        },
        body: JSON.stringify(next, null, 2),
      });

      if (!putRes.ok) {
        throw new Error(`PUT failed: ${putRes.status}`);
      }

      setData(next);
      setSaved(true);
    } catch (e) {
      console.error(e);
      setError("Save failed. Check your token, CORS, and API route.");
    } finally {
      setSaving(false);
    }
  };

  const removeMonth = async (k) => {
    if (!window.confirm(`Delete ${k}?`)) return;

    setSaved(false);
    setSaving(true);
    setError("");

    const next = { ...(data || {}) };
    delete next[k];

    try {
      const presignRes = await fetch(ADMIN_ENDPOINT, {
        headers: { "x-admin-token": ADMIN_TOKEN },
      });

      if (!presignRes.ok) {
        throw new Error(`Presign failed: ${presignRes.status}`);
      }

      const { url } = await presignRes.json();

      const putRes = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "max-age=60, must-revalidate",
        },
        body: JSON.stringify(next, null, 2),
      });

      if (!putRes.ok) {
        throw new Error(`PUT failed: ${putRes.status}`);
      }

      setData(next);

      // Clear form if you deleted the one currently loaded
      if (k === month) {
        setMonth("");
        setCode("");
        setLink("");
        setExpires("");
      }

      setSaved(true);
    } catch (e) {
      console.error(e);
      setError("Delete failed. Check your token, CORS, and API route.");
    } finally {
      setSaving(false);
    }
  };

  const editMonth = (k) => {
    const m = data?.[k];

    if (!m) return;

    setMonth(k);
    setCode(m.code || "");
    setLink(m.link || "");
    setExpires(m.expires || "");
    setSaved(false);
    setError("");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <p className="admin-loading">Loading…</p>;

  const monthKeys = Object.keys(data || {})
    .filter((k) => isStillValid(data[k]))
    .sort();

  // Blue button inline style (matches your theme, with hover/focus)
  const blueBtnBase = {
    backgroundColor: "#1e40af",
    borderColor: "#1e40af",
    color: "#fff",
  };

  return (
    <div className="admin-card">
      <h1 className="admin-title">Gate Access Admin</h1>

      <p className="admin-subtitle">
        Add, update, or delete months. Click <b>Save</b> to publish immediately.
      </p>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {saved && (
        <div className="admin-alert admin-alert-success">
          Saved! The app will use the new data automatically.
        </div>
      )}

      {/* Form (blank by default) */}
      <div className="admin-form">
        <label className="admin-label" htmlFor="month">
          Month and Year
        </label>

        <input
          id="month"
          type="text"
          className="admin-input"
          placeholder="YYYY-MM"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          maxLength={7}
        />

        <label className="admin-label" htmlFor="code">
          Gate Code
        </label>

        <input
          id="code"
          className="admin-input"
          placeholder="12345"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
        />

        <label className="admin-label" htmlFor="link">
          Tap-to-open Link
        </label>

        <input
          id="link"
          className="admin-input"
          placeholder="https://pass.myq.com/..."
          value={link}
          onChange={(e) => setLink(e.target.value)}
          inputMode="url"
        />

        <label className="admin-label" htmlFor="expires">
          Expiration Date
        </label>

        <input
          id="expires"
          type="text"
          className="admin-input"
          placeholder="MM/DD/YYYY"
          value={expires}
          onChange={(e) => setExpires(e.target.value)}
          maxLength={10}
        />

        <div className="admin-actions">
          <button
            className="btn btn-primary"
            onClick={saveMonth}
            disabled={saving}
            style={blueBtnBase}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                "0 6px 18px rgba(30,64,175,0.35)";
              e.currentTarget.style.opacity = "0.95";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.opacity = "1";
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow =
                "0 0 0 3px rgba(30,64,175,0.25)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Existing months */}
      <h2 className="admin-section-title">Existing Months</h2>

      <ul className="admin-list">
        {monthKeys.map((k) => (
          <li key={k} className="admin-list-item">
            <div className="admin-list-text">
              <b>{niceMonthLabel(k)}</b> — code: {data[k]?.code} — expires:{" "}
              {data[k]?.expires}
            </div>

            <div className="admin-actions">
              <button className="btn" onClick={() => editMonth(k)}>
                Edit
              </button>

              <button className="btn btn-danger" onClick={() => removeMonth(k)}>
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
